/**
 * theme-customizations.ts
 * 
 * Stores per-preset color token overrides in Firestore.
 * Firestore path: siteSettings/theme-customizations
 * 
 * Structure:
 *   { [themeId]: { colors: Partial<ColorTokens>, darkColors?: Partial<ColorTokens>, lightColors?: Partial<ColorTokens> }, updatedAt, updatedBy }
 */
import { doc, getDoc, setDoc, onSnapshot } from 'firebase/firestore';
import { db } from '@/lib/firebase';

export interface ThemeColorOverride {
  colors?: Record<string, string>;       // overrides for the main tokens
  darkColors?: Record<string, string>;   // overrides for darkTokens (system-reactive)
  lightColors?: Record<string, string>;  // overrides for lightTokens
}

export interface ThemeCustomizations {
  [themeId: string]: ThemeColorOverride | unknown;
  updatedAt?: Date | unknown;
  updatedBy?: string;
}

const DOC_PATH = 'siteSettings/theme-customizations';

export async function getThemeCustomizations(): Promise<Record<string, ThemeColorOverride>> {
  try {
    const snap = await getDoc(doc(db, DOC_PATH));
    if (!snap.exists()) return {};
    const data = snap.data();
    const result: Record<string, ThemeColorOverride> = {};
    Object.entries(data).forEach(([key, value]) => {
      if (key !== 'updatedAt' && key !== 'updatedBy' && typeof value === 'object' && value !== null) {
        result[key] = value as ThemeColorOverride;
      }
    });
    return result;
  } catch {
    return {};
  }
}

export async function saveThemeCustomization(
  themeId: string,
  overrides: ThemeColorOverride,
  userId?: string,
): Promise<void> {
  const snap = await getDoc(doc(db, DOC_PATH));
  const existing = snap.exists() ? snap.data() : {};
  await setDoc(doc(db, DOC_PATH), {
    ...existing,
    [themeId]: overrides,
    updatedAt: new Date(),
    updatedBy: userId || null,
  }, { merge: true });
}

export async function resetThemeCustomization(themeId: string, userId?: string): Promise<void> {
  const snap = await getDoc(doc(db, DOC_PATH));
  if (!snap.exists()) return;
  const data = snap.data();
  delete data[themeId];
  await setDoc(doc(db, DOC_PATH), {
    ...data,
    updatedAt: new Date(),
    updatedBy: userId || null,
  });
}

export function subscribeThemeCustomizations(
  callback: (c: Record<string, ThemeColorOverride>) => void,
): () => void {
  return onSnapshot(doc(db, DOC_PATH), (snap) => {
    if (snap.exists()) {
      const data = snap.data();
      const result: Record<string, ThemeColorOverride> = {};
      Object.entries(data).forEach(([key, value]) => {
        if (key !== 'updatedAt' && key !== 'updatedBy' && typeof value === 'object' && value !== null) {
          result[key] = value as ThemeColorOverride;
        }
      });
      callback(result);
    } else {
      callback({});
    }
  }, () => callback({}));
}
